import intl from "react-intl-universal";
import { formatFilterDate, startOfDay, endOfDay } from "../../../../Components/Molecules/GridHistory/gridFilterUtils";

export const BAAS_ONBOARD_STATUSES = [
  "pending",
  "contact_steps",
  "veriff",
  "completed",
  "expired",
  "revoked",
];

export const ONBOARD_STATUS_LABEL_KEYS = {
  pending: "baas.entidades.detail.verifications.inviteStatus.pending",
  contact_steps: "baas.entidades.detail.verifications.inviteStatus.contactSteps",
  veriff: "baas.entidades.detail.verifications.inviteStatus.veriff",
  completed: "baas.entidades.detail.verifications.inviteStatus.completed",
  expired: "baas.entidades.detail.verifications.inviteStatus.expired",
  revoked: "baas.entidades.detail.verifications.inviteStatus.revoked",
};

export const ONBOARD_STATUS_BADGE_STYLES = {
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  contact_steps: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
  veriff: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400",
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  expired: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  revoked: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

export const EMPTY_BAAS_ONBOARD_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  statuses: [],
};

export const mapOnboardInvite = (raw) => ({
  inviteId: raw?.invite_id ?? raw?.inviteId ?? null,
  token: raw?.token ?? "",
  entityId: raw?.entity_id ?? raw?.entityId ?? null,
  pendingName: raw?.pending_name ?? raw?.pendingName ?? null,
  status: String(raw?.status || "pending").toLowerCase(),
  path: raw?.path ?? (raw?.token ? `/auth/onboard/${raw.token}` : ""),
  createdAt: raw?.created_at ?? raw?.createdAt ?? null,
  expiresAt: raw?.expires_at ?? raw?.expiresAt ?? null,
  completedAt: raw?.completed_at ?? raw?.completedAt ?? null,
});

export const buildInviteUrl = (pathOrToken) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = String(pathOrToken || "").startsWith("/")
    ? pathOrToken
    : `/auth/onboard/${pathOrToken}`;
  return `${origin}${path}`;
};

export const formatOnboardInviteDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

export const canRevokeOnboardInvite = (invite) =>
  invite?.status !== "revoked" &&
  invite?.status !== "completed" &&
  invite?.status !== "expired";

export const getBaasOnboardStatusOptions = () =>
  BAAS_ONBOARD_STATUSES.map((status) => ({
    id: status,
    label: intl.get(ONBOARD_STATUS_LABEL_KEYS[status]) || status,
  }));

const matchesSearch = (invite, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  const url = buildInviteUrl(invite.path || invite.token).toLowerCase();
  return [invite.token, invite.path, invite.entityId, invite.pendingName, url]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDateRange = (invite, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const createdAt = new Date(invite.createdAt);
  if (Number.isNaN(createdAt.getTime())) return false;
  if (dateFrom && createdAt < startOfDay(dateFrom)) return false;
  if (dateTo && createdAt > endOfDay(dateTo)) return false;
  return true;
};

export const filterBaasOnboardInvites = (items = [], filters = EMPTY_BAAS_ONBOARD_FILTERS) =>
  items.filter((invite) => {
    if (!matchesSearch(invite, filters.searchQuery)) return false;
    if (!matchesDateRange(invite, filters.dateFrom, filters.dateTo)) return false;
    if (filters.statuses?.length && !filters.statuses.includes(invite.status)) return false;
    return true;
  });

export const hasActiveBaasOnboardFilters = (filters = EMPTY_BAAS_ONBOARD_FILTERS) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.statuses?.length,
  );

export const isBaasOnboardChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "status":
      return Boolean(filters.statuses?.length);
    default:
      return false;
  }
};

export const buildBaasOnboardFilterTags = (filters = EMPTY_BAAS_ONBOARD_FILTERS) => {
  const tags = [];
  const statusOptions = getBaasOnboardStatusOptions();

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const fromLabel = filters.dateFrom ? formatFilterDate(filters.dateFrom) : "…";
    const toLabel = filters.dateTo ? formatFilterDate(filters.dateTo) : "…";
    tags.push({
      key: "date",
      iconType: "date",
      value: `${fromLabel} – ${toLabel}`,
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((statusId) => statusOptions.find((item) => item.id === statusId)?.label ?? statusId)
        .join(", "),
    });
  }

  return tags;
};

export const removeBaasOnboardFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    case "date":
      return { ...filters, dateFrom: null, dateTo: null };
    case "statuses":
      return { ...filters, statuses: [] };
    default:
      return filters;
  }
};

export const resolveOnboardProxyError = (error) => {
  const detail =
    error?.detail ||
    error?.response?.detail ||
    error?.body?.detail ||
    error?.data?.detail ||
    error?.message;
  if (detail && String(detail).trim()) return String(detail).trim();
  return intl.get("baas.entidades.quickActions.onboardError");
};
